/**
 * Data hooks of the card form (2.M deliverable 10). Reads: `docs.meta`, `types.list` and
 * `rules.get` (key `['card', docId]`; a 404 is the designed "no card yet", cached as null). Writes:
 * `docs.create`, `rules.create`, `rules.update` and (Customize, s3) `variants.create`.
 * `doc.changed` of the page refetches the card.
 */
import {
  ApiError,
  type DocMetaV1,
  type DocTypeSummaryV1,
  docsCreate,
  docsMeta,
  type RulesRecordV1,
  rulesCreate,
  rulesGet,
  rulesUpdate,
  typesList,
  variantsCreate,
} from '@codex/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../../../auth/client.js';
import { appEvents } from '../../../editor-services/events.js';
import { type CardCustomizeFields, variantRequest } from '../model/customize.js';
import type { CardKind, CardState } from '../model/draft.js';
import { savePlan } from '../model/save.js';

export type LoadFailure = 'missing' | 'failed';

/** A 404 is `missing`, anything else `failed`. */
export function failureOf(error: unknown): LoadFailure {
  return error instanceof ApiError && error.status === 404 ? 'missing' : 'failed';
}

export const cardKey = (docId: string) => ['card', docId] as const;

/**
 * The card kind of a type, as s1's `cardKindOfType`: a canon type with `has_card` keyed `spell` or
 * `item` (the server also requires a built-in type; only the built-in ones carry those keys).
 */
export function kindOfType(type: DocTypeSummaryV1 | undefined): CardKind | null {
  if (type?.library !== 'canon' || !type.capabilities.includes('has_card')) return null;
  return type.key === 'spell' || type.key === 'item' ? type.key : null;
}

export interface CardDoc {
  readonly doc: DocMetaV1 | undefined;
  readonly type: DocTypeSummaryV1 | undefined;
  readonly kind: CardKind | null;
  /** A live canon page whose type has `has_card`. */
  readonly editable: boolean;
  readonly loading: boolean;
  readonly failure: LoadFailure | null;
  readonly retry: () => void;
}

export function useCardDoc(docId: string, enabled: boolean): CardDoc {
  const meta = useQuery({
    queryKey: ['meta', docId],
    queryFn: () => api(docsMeta, { params: { id: docId } }),
    enabled,
    retry: false,
  });
  const types = useTypes(enabled);
  const doc = meta.data;
  const type = doc ? types.data?.find((t) => t.id === doc.typeId) : undefined;
  const kind = kindOfType(type);
  const error = meta.isError ? meta.error : types.isError ? types.error : null;
  return {
    doc,
    type,
    kind,
    editable: doc?.library === 'canon' && doc.deletedAt === null && kind !== null,
    loading: enabled && error === null && (doc === undefined || types.data === undefined),
    failure: error === null ? null : failureOf(error),
    retry: () => {
      void meta.refetch();
      void types.refetch();
    },
  };
}

/** `rules.get`; null when the page has no card yet. */
export async function fetchCard(docId: string): Promise<RulesRecordV1 | null> {
  try {
    return await api(rulesGet, { params: { id: docId } });
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** The page's card, refetched on `doc.changed` of the page. */
export function useCard(docId: string, enabled: boolean) {
  const client = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    return appEvents.subscribeDocChanged(docId, () => {
      void client.invalidateQueries({ queryKey: cardKey(docId) });
    });
  }, [client, docId, enabled]);
  return useQuery({
    queryKey: cardKey(docId),
    queryFn: () => fetchCard(docId),
    enabled,
    retry: false,
  });
}

export interface SaveInput {
  readonly state: CardState;
  readonly saved: RulesRecordV1 | null;
}

/** Runs `savePlan`; resolves the answer's card, or null when there was nothing to send. */
export function useSaveCard(docId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ state, saved }: SaveInput): Promise<RulesRecordV1 | null> => {
      const plan = savePlan(state, saved);
      if (plan.kind === 'none') return null;
      return plan.kind === 'create'
        ? api(rulesCreate, { params: { id: docId }, body: plan.body })
        : api(rulesUpdate, { params: { id: docId }, body: plan.body });
    },
    onSuccess: (card) => {
      if (card) client.setQueryData(cardKey(docId), card);
    },
  });
}

/** New spell and New item: `docs.create` of a page of that type; resolves its id. */
export async function createCardPage(kind: CardKind, title: string): Promise<string> {
  return (await api(docsCreate, { body: { typeKey: kind, title } })).id;
}

/** Customize: `variants.create` of a card copy from the SRD entry `sourceDocId`. */
export function useCreateCardVariant(sourceDocId: string) {
  return useMutation({
    mutationFn: (fields: CardCustomizeFields) =>
      api(variantsCreate, { params: { id: sourceDocId }, body: variantRequest(fields) }),
  });
}

/** The live types (`types.list`, key `['types']`, shared with the stat-block form). */
export function useTypes(enabled = true) {
  return useQuery({
    queryKey: ['types'],
    queryFn: async () => (await api(typesList)).items,
    enabled,
    retry: false,
  });
}
